import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { API } from "../App";

export default function SearchBar({ 
  variant = "default", 
  placeholder = "Search videographers, editors, cinematographers...",
  className = "",
  onSearch = null 
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Popular searches
  const popularSearches = [
    "Videographer",
    "Cinematographer",
    "Editor",
    "Drone Operator",
    "Sound Recordist",
    "Colorist"
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/browse?query=${encodeURIComponent(searchQuery)}`);
    }
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const isHero = variant === "hero";

  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative flex items-center ${
          isHero
            ? "bg-zinc-900/80 border border-zinc-700 backdrop-blur-sm"
            : "bg-zinc-900 border border-zinc-800"
        }`}
      >
        <Search className={`absolute left-4 h-5 w-5 text-zinc-500 ${isHero ? "text-zinc-400" : ""}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full bg-transparent pl-12 pr-4 outline-none text-white placeholder-zinc-500 ${
            isHero ? "py-4 text-lg" : "py-3"
          }`}
          data-testid="search-input"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-20 text-zinc-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <Button
          onClick={() => handleSearch()}
          className={`absolute right-2 bg-orange-600 hover:bg-orange-500 ${
            isHero ? "px-6" : "px-4"
          }`}
          data-testid="search-btn"
        >
          {isHero ? (
            <>
              SEARCH <ArrowRight className="ml-2 h-4 w-4" />
            </>
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 shadow-xl z-50"
          data-testid="search-suggestions"
        >
          {loading ? (
            <div className="p-4 text-center text-zinc-500">
              <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {suggestions.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-2 text-xs font-mono text-zinc-500 uppercase tracking-wider">
                    Suggestions
                  </p>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(suggestion)}
                      className="w-full text-left px-3 py-2 text-zinc-300 hover:bg-zinc-800 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
              <div className="p-2 border-t border-zinc-800">
                <p className="px-3 py-2 text-xs font-mono text-zinc-500 uppercase tracking-wider">
                  Popular Searches
                </p>
                <div className="flex flex-wrap gap-2 px-3 pb-2">
                  {popularSearches.map((search) => (
                    <button
                      key={search}
                      onClick={() => handleSearch(search)}
                      className="px-3 py-1 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                    >
                      {search}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
